import {
    ComponentProps,
    FC,
    PointerEvent as ReactPointerEvent,
    useEffect,
    useRef,
    useState,
} from 'react';

import { ArrowRight, Info, Minus, Plus, ShoppingCart } from 'lucide-react';

import { OfferPhotoGallery } from '../../components/shared/OfferPhotoGallery';
import { OfferSellerSummary } from '../../components/shared/OfferSellerSummary';
import { Badge, badgeVariants } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/Tooltip';
import { useCart } from '../../context/cart-context';
import { useOfferViewTracking } from '../../hooks/useOfferViewTracking';
import { Offer, OfferProperty } from '../../types';
import { cn } from '../../utils';

interface Props {
    offers: Offer[];
    offerProperties: OfferProperty[];
}

interface OfferQuantityInputProps
    extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
    value: number;
    onChangeValue: (val: number) => void;
}

const OfferQuantityInput: FC<OfferQuantityInputProps> = ({
    value,
    onChangeValue,
    onFocus,
    onBlur,
    ...props
}) => {
    const [localValue, setLocalValue] = useState(value.toString());
    const [prevValue, setPrevValue] = useState(value);

    // Sync with external value changes (e.g. clamping)
    if (value !== prevValue) {
        setPrevValue(value);
        setLocalValue(value.toString());
    }

    return (
        <Input
            {...props}
            value={localValue}
            onFocus={(e) => {
                setLocalValue('');
                onFocus?.(e);
            }}
            onBlur={(e) => {
                setLocalValue(value.toString());
                onBlur?.(e);
            }}
            onChange={(e) => {
                const val = e.target.value;
                setLocalValue(val);
                const parsed = Number.parseInt(val);
                if (!Number.isNaN(parsed)) {
                    onChangeValue(parsed);
                }
            }}
        />
    );
};

const normalizeHint = (hint?: string | null) => {
    const value = hint?.trim();

    return value ? value : null;
};

const OfferPropertyHintContent: FC<{ hint: string }> = ({ hint }) => (
    <TooltipContent side="top" align="start" className="max-w-xs break-words">
        <p>{hint}</p>
    </TooltipContent>
);

const toggleClickTooltip = (
    event: ReactPointerEvent<HTMLButtonElement>,
    shouldUseClickTooltip: boolean,
    tooltipKey: string,
    activeTooltipKey: string | null,
    onActiveTooltipChange: (key: string | null) => void,
) => {
    if (!shouldUseClickTooltip) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    onActiveTooltipChange(activeTooltipKey === tooltipKey ? null : tooltipKey);
};

const OfferPropertyHeader: FC<{
    property: OfferProperty;
    shouldUseClickTooltip: boolean;
    activeTooltipKey: string | null;
    onActiveTooltipChange: (key: string | null) => void;
}> = ({
    property,
    shouldUseClickTooltip,
    activeTooltipKey,
    onActiveTooltipChange,
}) => {
    const propertyHint = normalizeHint(property.hint);
    const tooltipKey = `property-${property.slug}`;

    return (
        <th className="p-2 text-left text-sm font-medium text-muted-foreground">
            {propertyHint ? (
                <Tooltip
                    delayDuration={0}
                    open={
                        shouldUseClickTooltip
                            ? activeTooltipKey === tooltipKey
                            : undefined
                    }
                >
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            aria-label={`Показати підказку: ${property.name}`}
                            className="inline-flex cursor-help items-center gap-1 bg-transparent p-0 text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                            onPointerDown={(event) =>
                                toggleClickTooltip(
                                    event,
                                    shouldUseClickTooltip,
                                    tooltipKey,
                                    activeTooltipKey,
                                    onActiveTooltipChange,
                                )
                            }
                        >
                            <span>{property.name}</span>
                            <Info className="h-3.5 w-3.5" />
                        </button>
                    </TooltipTrigger>
                    <OfferPropertyHintContent hint={propertyHint} />
                </Tooltip>
            ) : (
                property.name
            )}
        </th>
    );
};

const CollectionPropertyValue: FC<{
    displayValue: string;
    title: string;
    hint: string | null;
    tooltipKey: string;
    shouldUseClickTooltip: boolean;
    activeTooltipKey: string | null;
    onActiveTooltipChange: (key: string | null) => void;
}> = ({
    displayValue,
    title,
    hint,
    tooltipKey,
    shouldUseClickTooltip,
    activeTooltipKey,
    onActiveTooltipChange,
}) => {
    if (!hint) {
        return (
            <Badge variant="outline" className="text-xs">
                {displayValue}
            </Badge>
        );
    }

    return (
        <Tooltip
            delayDuration={0}
            open={
                shouldUseClickTooltip
                    ? activeTooltipKey === tooltipKey
                    : undefined
            }
        >
            <TooltipTrigger
                type="button"
                aria-label={`Показати підказку: ${title}`}
                className={cn(
                    badgeVariants({
                        variant: 'outline',
                    }),
                    'cursor-help gap-1 text-xs',
                )}
                onPointerDown={(event) =>
                    toggleClickTooltip(
                        event,
                        shouldUseClickTooltip,
                        tooltipKey,
                        activeTooltipKey,
                        onActiveTooltipChange,
                    )
                }
            >
                {displayValue}
                <Info className="h-3 w-3" />
            </TooltipTrigger>
            <OfferPropertyHintContent hint={hint} />
        </Tooltip>
    );
};

export const Offers: FC<Props> = ({ offers, offerProperties }) => {
    const {
        cartItems,
        getOfferQuantity,
        getTooltipMessage,
        updateOfferQuantity,
        handleAddToCart,
        canAddQuantity,
        isOwnOffer,
        canAddToCart,
    } = useCart();

    const [canScrollRight, setCanScrollRight] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [isNarrowViewport, setIsNarrowViewport] = useState(false);
    const [activeTextTooltipKey, setActiveTextTooltipKey] = useState<
        string | null
    >(null);
    const [activeHintTooltipKey, setActiveHintTooltipKey] = useState<
        string | null
    >(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const observeOfferView = useOfferViewTracking<HTMLTableRowElement>();

    const checkScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for tolerance

        if (scrollLeft > 0) {
            setHasScrolled(true);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [offers]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mediaQuery = window.matchMedia(
            '(hover: none), (pointer: coarse)',
        );
        const syncTouchPreference = () => {
            setIsTouchDevice(mediaQuery.matches);
        };

        syncTouchPreference();

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', syncTouchPreference);
            return () =>
                mediaQuery.removeEventListener('change', syncTouchPreference);
        }

        mediaQuery.addListener(syncTouchPreference);
        return () => mediaQuery.removeListener(syncTouchPreference);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const syncViewportSize = () => {
            setIsNarrowViewport(window.innerWidth < 768);
        };

        syncViewportSize();
        window.addEventListener('resize', syncViewportSize);
        return () => window.removeEventListener('resize', syncViewportSize);
    }, []);

    useEffect(() => {
        if (!activeHintTooltipKey) return;

        const closeHintTooltip = () => setActiveHintTooltipKey(null);
        const closeHintTooltipOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeHintTooltip();
            }
        };

        document.addEventListener('pointerdown', closeHintTooltip);
        document.addEventListener('keydown', closeHintTooltipOnEscape);

        return () => {
            document.removeEventListener('pointerdown', closeHintTooltip);
            document.removeEventListener('keydown', closeHintTooltipOnEscape);
        };
    }, [activeHintTooltipKey]);

    const shouldUseClickTooltip = isTouchDevice || isNarrowViewport;
    const hasConditionProperty = offerProperties.some(
        (property) => property.slug === 'condition',
    );

    return (
        <TooltipProvider delayDuration={300}>
            <div className="mt-8">
                {!!offers?.length && (
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-lg font-medium">
                            Доступні пропозиції ({offers?.length})
                        </h2>
                    </div>
                )}

                {offers?.length ? (
                    <div className="relative">
                        {/* Scroll Hint Animation */}
                        <div
                            className={`pointer-events-none absolute top-0 right-1 bottom-0 z-20 flex items-center justify-center transition-opacity duration-500 ${
                                canScrollRight && !hasScrolled
                                    ? 'opacity-100'
                                    : 'opacity-0'
                            }`}
                        >
                            <div className="flex animate-pulse items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-md">
                                <span>Гортайте</span>
                                <ArrowRight className="h-3 w-3" />
                            </div>
                        </div>
                        <div
                            className="overflow-x-auto"
                            ref={scrollContainerRef}
                            onScroll={checkScroll}
                        >
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="bg-muted/50">
                                        <th className="p-2 text-left text-sm font-medium text-muted-foreground">
                                            Продавець
                                        </th>

                                        {offerProperties &&
                                            offerProperties.map((property) => (
                                                <OfferPropertyHeader
                                                    key={property.slug}
                                                    property={property}
                                                    shouldUseClickTooltip={
                                                        shouldUseClickTooltip
                                                    }
                                                    activeTooltipKey={
                                                        activeHintTooltipKey
                                                    }
                                                    onActiveTooltipChange={
                                                        setActiveHintTooltipKey
                                                    }
                                                />
                                            ))}

                                        <th className="p-2 text-left text-sm font-medium text-muted-foreground">
                                            К-сть
                                        </th>
                                        <th className="p-2 text-left text-sm font-medium text-muted-foreground">
                                            <div className="flex items-center">
                                                Ціна
                                            </div>
                                        </th>
                                        <th className="p-2 text-right text-sm font-medium text-muted-foreground"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {offers.map((offer) => (
                                        <tr
                                            ref={observeOfferView}
                                            key={offer.id}
                                            data-offer-id={offer.id}
                                            className="transition-colors hover:bg-muted/30"
                                        >
                                            <td className="p-2">
                                                <OfferSellerSummary
                                                    sellerName={
                                                        offer.seller_name
                                                    }
                                                    sellerNickname={
                                                        offer.user.nickname
                                                    }
                                                    sellerPath={
                                                        offer.user.url_path
                                                    }
                                                    sellerAvatar={
                                                        offer.user.avatar
                                                    }
                                                    completedSalesCount={
                                                        offer.seller_completed_orders_count
                                                    }
                                                    premiumActive={
                                                        offer.premium_active
                                                    }
                                                    showPremiumIcon
                                                    size="default"
                                                />
                                            </td>
                                            {offerProperties &&
                                                offerProperties.map(
                                                    (property) => {
                                                        const propertyValue =
                                                            offer.property_set[
                                                                property.slug
                                                            ];
                                                        const selectedOption =
                                                            property.kind ===
                                                            'collection'
                                                                ? property.offer_property_options.find(
                                                                      (
                                                                          option,
                                                                      ) =>
                                                                          option.slug ===
                                                                          propertyValue,
                                                                  )
                                                                : undefined;
                                                        const displayValue =
                                                            property.kind ===
                                                            'collection'
                                                                ? selectedOption?.name ||
                                                                  propertyValue ||
                                                                  '—'
                                                                : propertyValue ||
                                                                  '—';
                                                        const optionHint =
                                                            normalizeHint(
                                                                selectedOption?.hint,
                                                            );
                                                        const shouldShowTextTooltip =
                                                            property.kind !==
                                                                'collection' &&
                                                            displayValue !==
                                                                '—' &&
                                                            displayValue.length >
                                                                28;
                                                        const textTooltipKey = `${offer.id}-${property.slug}`;

                                                        return (
                                                            <td
                                                                key={
                                                                    property.slug
                                                                }
                                                                className="p-2"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {property.kind ===
                                                                    'collection' ? (
                                                                        <CollectionPropertyValue
                                                                            displayValue={
                                                                                displayValue
                                                                            }
                                                                            title={`${property.name}: ${displayValue}`}
                                                                            tooltipKey={`${offer.id}-${property.slug}-${propertyValue}`}
                                                                            hint={
                                                                                optionHint
                                                                            }
                                                                            shouldUseClickTooltip={
                                                                                shouldUseClickTooltip
                                                                            }
                                                                            activeTooltipKey={
                                                                                activeHintTooltipKey
                                                                            }
                                                                            onActiveTooltipChange={
                                                                                setActiveHintTooltipKey
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <>
                                                                            {shouldShowTextTooltip ? (
                                                                                <Tooltip
                                                                                    delayDuration={
                                                                                        0
                                                                                    }
                                                                                    open={
                                                                                        shouldUseClickTooltip
                                                                                            ? activeTextTooltipKey ===
                                                                                              textTooltipKey
                                                                                            : undefined
                                                                                    }
                                                                                    onOpenChange={
                                                                                        shouldUseClickTooltip
                                                                                            ? (
                                                                                                  nextOpen,
                                                                                              ) =>
                                                                                                  setActiveTextTooltipKey(
                                                                                                      nextOpen
                                                                                                          ? textTooltipKey
                                                                                                          : null,
                                                                                                  )
                                                                                            : undefined
                                                                                    }
                                                                                >
                                                                                    <TooltipTrigger
                                                                                        asChild
                                                                                    >
                                                                                        <button
                                                                                            type="button"
                                                                                            className="block max-w-[10rem] cursor-help truncate bg-transparent p-0 text-left text-sm text-foreground sm:max-w-[14rem] md:max-w-[18rem]"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    !shouldUseClickTooltip
                                                                                                ) {
                                                                                                    return;
                                                                                                }

                                                                                                setActiveTextTooltipKey(
                                                                                                    textTooltipKey,
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                displayValue
                                                                                            }
                                                                                        </button>
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent
                                                                                        side="top"
                                                                                        align="start"
                                                                                        className="max-w-xs break-words"
                                                                                    >
                                                                                        <p>
                                                                                            {
                                                                                                displayValue
                                                                                            }
                                                                                        </p>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                            ) : (
                                                                                <span className="block max-w-[10rem] truncate text-sm text-foreground sm:max-w-[14rem] md:max-w-[18rem]">
                                                                                    {
                                                                                        displayValue
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {property.slug ===
                                                                        'condition' && (
                                                                        <OfferPhotoGallery
                                                                            photos={
                                                                                offer.photos
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    },
                                                )}
                                            <td className="p-2 text-sm">
                                                {offer.amount}
                                            </td>
                                            <td className="p-2 font-medium">
                                                ₴{offer.price.toFixed(2)}
                                            </td>
                                            <td className="p-2">
                                                <div className="flex flex-row justify-end gap-2">
                                                    {!hasConditionProperty && (
                                                        <OfferPhotoGallery
                                                            photos={
                                                                offer.photos
                                                            }
                                                        />
                                                    )}
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-6 w-6 bg-transparent"
                                                            onClick={() =>
                                                                updateOfferQuantity(
                                                                    offer.id,
                                                                    getOfferQuantity(
                                                                        offer.id,
                                                                    ) - 1,
                                                                )
                                                            }
                                                            disabled={
                                                                isOwnOffer(
                                                                    offer,
                                                                ) ||
                                                                getOfferQuantity(
                                                                    offer.id,
                                                                ) <= 1
                                                            }
                                                        >
                                                            <Minus className="h-3 w-3" />
                                                        </Button>
                                                        <OfferQuantityInput
                                                            type="number"
                                                            min="1"
                                                            disabled={isOwnOffer(
                                                                offer,
                                                            )}
                                                            max={(() => {
                                                                const existingItem =
                                                                    cartItems.find(
                                                                        (
                                                                            item,
                                                                        ) =>
                                                                            item.id ===
                                                                            offer.id,
                                                                    );
                                                                return existingItem
                                                                    ? offer.amount -
                                                                          existingItem.quantity
                                                                    : offer.amount;
                                                            })()}
                                                            value={getOfferQuantity(
                                                                offer.id,
                                                            )}
                                                            onChangeValue={(
                                                                val,
                                                            ) =>
                                                                updateOfferQuantity(
                                                                    offer.id,
                                                                    val,
                                                                )
                                                            }
                                                            className="h-6 w-12 px-1 text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                        />
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-6 w-6 bg-transparent"
                                                            onClick={() =>
                                                                updateOfferQuantity(
                                                                    offer.id,
                                                                    getOfferQuantity(
                                                                        offer.id,
                                                                    ) + 1,
                                                                )
                                                            }
                                                            disabled={
                                                                isOwnOffer(
                                                                    offer,
                                                                ) ||
                                                                !canAddQuantity(
                                                                    offer,
                                                                    getOfferQuantity(
                                                                        offer.id,
                                                                    ) + 1,
                                                                )
                                                            }
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Tooltip
                                                            delayDuration={300}
                                                        >
                                                            <TooltipTrigger
                                                                asChild
                                                            >
                                                                <div>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        className="h-8 bg-transparent px-2 text-xs"
                                                                        onClick={() =>
                                                                            handleAddToCart(
                                                                                offer,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !canAddToCart(
                                                                                offer,
                                                                            ) ||
                                                                            !canAddQuantity(
                                                                                offer,
                                                                                1,
                                                                            )
                                                                        }
                                                                    >
                                                                        <ShoppingCart className="mr-1 h-3 w-3" />
                                                                        {isOwnOffer(
                                                                            offer,
                                                                        )
                                                                            ? 'Ваша пропозиція'
                                                                            : 'В кошик'}
                                                                    </Button>
                                                                </div>
                                                            </TooltipTrigger>
                                                            {getTooltipMessage(
                                                                offer,
                                                            ) && (
                                                                <TooltipContent
                                                                    side="top"
                                                                    align="center"
                                                                >
                                                                    <p>
                                                                        {getTooltipMessage(
                                                                            offer,
                                                                        )}
                                                                    </p>
                                                                </TooltipContent>
                                                            )}
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-lg bg-muted/30 py-4 text-center">
                        <p className="mb-3 text-muted-foreground">
                            Наразі немає пропозицій для цього товару.
                        </p>
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};
